import * as React from 'react';
import { DropdownItemGroup, DropdownItem } from '@atlaskit/dropdown-menu';
import { FormattedMessage } from 'react-intl';
import { InteractiveElement } from 'view/common/interactive-element';
import { getRequestListUrl } from 'util/url';

import messages from './messages';
import { MenuItemBadge } from './styled';
import { NoUnderlineLink } from '../styled';

const EXCLUDE_CUSTOMISATION_CLASS = 'cv-exclude-from-customisation';

const createdByMeMenuItem = (myRequestCount: number) => {
    const requestListUrl = getRequestListUrl({
        page: 1,
        statuses: ['open'],
    });

    return (
        <NoUnderlineLink to={requestListUrl} id="myrequestsmenuitem" actionSubjectId="navbarCreatedByMe">
            <DropdownItem className={EXCLUDE_CUSTOMISATION_CLASS}>
                {myRequestCount > 0 ? <MenuItemBadge>{myRequestCount}</MenuItemBadge> : undefined}
                <FormattedMessage {...messages.createdByMe} />
            </DropdownItem>
        </NoUnderlineLink>
    );
};

const allRequestsMenuItem = () => {
    const allRequestsPageUrl = getRequestListUrl({
        page: 1,
        reporter: 'all',
        statuses: ['open'],
    });

    return (
        <NoUnderlineLink to={allRequestsPageUrl} id="allrequestsmenuitem" actionSubjectId="navbarAllRequests">
            <DropdownItem id="allrequestsmenuitem" className={EXCLUDE_CUSTOMISATION_CLASS}>
                <FormattedMessage {...messages.all} />
            </DropdownItem>
        </NoUnderlineLink>
    );
};

const approvalMenuItem = (pendingApprovalCount: number) =>
    pendingApprovalCount ? myPendingApprovalMenuItem(pendingApprovalCount) : myApprovalMenuItem();

const approvalLinkFor = (approvalQueryType: string) => {
    return `/user/approvals?approvalQueryType=${approvalQueryType}&page=1`;
};

const myPendingApprovalMenuItem = (pendingApprovalCount: number) => {
    return (
        <NoUnderlineLink
            to={approvalLinkFor('myPending')}
            id="approvalsMenuItem"
            actionSubjectId="navbarMyPendingApprovals"
        >
            <DropdownItem id="approvalsMenuItem" className={EXCLUDE_CUSTOMISATION_CLASS}>
                <MenuItemBadge>{pendingApprovalCount}</MenuItemBadge>
                <FormattedMessage {...messages.approvals} />
            </DropdownItem>
        </NoUnderlineLink>
    );
};

const myApprovalMenuItem = () => {
    return (
        <NoUnderlineLink to={approvalLinkFor('myApproval')} id="approvalsMenuItem" actionSubjectId="navbarMyApprovals">
            <DropdownItem id="approvalsMenuItem" className={EXCLUDE_CUSTOMISATION_CLASS}>
                <FormattedMessage {...messages.approvals} />
            </DropdownItem>
        </NoUnderlineLink>
    );
};

const organisationMenuItems = (organisations: Organisation[]) => {
    return organisations.map((org) => organisationMenuItem(org));
};

const organisationMenuItem = (org: Organisation) => {
    const allOrgsRequestsPageUrl = getRequestListUrl({
        page: 1,
        reporter: `org-${org.id}`,
        statuses: ['open'],
    });

    return (
        <NoUnderlineLink
            key={`orgrequestsmenuitem-${org.id}`}
            to={allOrgsRequestsPageUrl}
            id={`orgrequestsmenuitem-${org.id}`}
            actionSubjectId="navbarOrganisation"
        >
            <DropdownItem id={`orgrequestsmenuitem-${org.id}`} className={EXCLUDE_CUSTOMISATION_CLASS}>
                {org.name}
            </DropdownItem>
        </NoUnderlineLink>
    );
};

interface RequestsMenuGroupProps {
    onItemClick: () => void;
    pendingApprovalCount: number;
    approvalCount: number;
    myRequestCount: number;
    organisations: Organisation[];
}

export interface Organisation {
    name: string;
    id: number;
}

const RequestsMenuGroup: React.SFC<RequestsMenuGroupProps> = (props) => {
    const { onItemClick, pendingApprovalCount, approvalCount, myRequestCount, organisations } = props;

    const showApproval = pendingApprovalCount > 0 || approvalCount > 0;

    return (
        <DropdownItemGroup title={<FormattedMessage {...messages.requests} />}>
            <InteractiveElement tagName="span" onClick={onItemClick} tabIndex={-1}>
                {createdByMeMenuItem(myRequestCount)}
                {showApproval ? approvalMenuItem(pendingApprovalCount) : undefined}
                {organisationMenuItems(organisations)}
                {allRequestsMenuItem()}
            </InteractiveElement>
        </DropdownItemGroup>
    );
};

export default RequestsMenuGroup;
